import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfColor,
  PdfPen,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { moduleRegistry } from '../../modules';
import { uiModule } from '../../modules/ui';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(8);

const PAGE_WIDTH = MM(210);
const PAGE_HEIGHT = MM(297);

export class TestUI implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, _opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    moduleRegistry.register(uiModule);
    const uiModule$1 = moduleRegistry.get('ui');
    if (!uiModule$1) throw new Error('UI module not found');

    const font = new PdfFont(PdfFont.FONT_NAME, 10);
    const titleFont = new PdfFont(PdfFont.FONT_NAME, 16);
    const labelFont = new PdfFont(PdfFont.FONT_NAME, 8);

    const title = (page: PageGen, text: string) =>
      page.drawString(
        text,
        titleFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, MM(5), MM(194), MM(10)),
        ETextAlign.CenterLeft
      );

    const sectionTitle = (page: PageGen, y: number, text: string) =>
      page.drawString(
        text,
        font,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, y, MM(194), MM(5)),
        ETextAlign.CenterLeft
      );

    const drawUi = (
      page: PageGen,
      x: number,
      y: number,
      width: number,
      height: number,
      xaml: string,
      attrs: Record<string, string> = {}
    ) => {
      const rect = new PdfRect(x, y, width, height);
      const outline = new PdfPen(PdfColor.fromRgb(220, 220, 220), 0.3);
      page.drawRectangle(outline, null, rect);
      const content = uiModule$1.parse(xaml, attrs);
      uiModule$1.draw(content, rect, ETextAlign.TopLeft, { font: labelFont, page });
    };

    const ROW1_Y = MM(25);
    const ROW2_Y = MM(95);
    const ROW3_Y = MM(165);
    const ROW4_Y = MM(235);

    const p1 = pdf.addPage();
    title(p1, 'UI Module — Basic Controls');

    sectionTitle(p1, ROW1_Y, 'Labels');
    drawUi(p1, MARGIN, ROW1_Y + MM(8), MM(50), MM(8), '<Label>Simple Label</Label>');
    drawUi(p1, MARGIN + MM(55), ROW1_Y + MM(8), MM(50), MM(8), '<Label>Another Label</Label>');
    drawUi(p1, MARGIN + MM(110), ROW1_Y + MM(8), MM(50), MM(8), '<Label>Third Label</Label>');

    sectionTitle(p1, ROW2_Y, 'TextBox');
    drawUi(p1, MARGIN, ROW2_Y + MM(8), MM(80), MM(10), '<TextBox/>');
    drawUi(p1, MARGIN + MM(85), ROW2_Y + MM(8), MM(120), MM(10), '<TextBox Width="120"/>');
    drawUi(p1, MARGIN, ROW2_Y + MM(22), MM(60), MM(10), '<TextBox Height="20"/>');

    sectionTitle(p1, ROW3_Y, 'Buttons');
    drawUi(p1, MARGIN, ROW3_Y + MM(8), MM(40), MM(12), '<Button>OK</Button>');
    drawUi(p1, MARGIN + MM(45), ROW3_Y + MM(8), MM(80), MM(12), '<Button>Submit</Button>');
    drawUi(
      p1,
      MARGIN + MM(130),
      ROW3_Y + MM(8),
      MM(60),
      MM(18),
      '<Button Height="18">Cancel</Button>'
    );

    sectionTitle(p1, ROW3_Y + MM(25), 'PasswordBox');
    drawUi(p1, MARGIN, ROW3_Y + MM(33), MM(80), MM(10), '<PasswordBox>secret</PasswordBox>');
    drawUi(
      p1,
      MARGIN + MM(85),
      ROW3_Y + MM(33),
      MM(100),
      MM(10),
      '<PasswordBox>another password</PasswordBox>'
    );

    sectionTitle(p1, ROW4_Y, 'CheckBox & RadioButton');
    drawUi(p1, MARGIN, ROW4_Y + MM(8), MM(60), MM(8), '<CheckBox>Option 1</CheckBox>');
    drawUi(
      p1,
      MARGIN + MM(65),
      ROW4_Y + MM(8),
      MM(60),
      MM(8),
      '<CheckBox IsChecked="true">Checked</CheckBox>'
    );
    drawUi(
      p1,
      MARGIN + MM(130),
      ROW4_Y + MM(8),
      MM(60),
      MM(8),
      '<RadioButton>Radio 1</RadioButton>'
    );

    const p2 = pdf.addPage();
    title(p2, 'UI Module — Containers');

    sectionTitle(p2, ROW1_Y, 'StackPanel (Vertical)');
    drawUi(
      p2,
      MARGIN,
      ROW1_Y + MM(8),
      MM(100),
      MM(45),
      `<StackPanel Orientation="Vertical">
        <Label>Item 1</Label>
        <Label>Item 2</Label>
        <Label>Item 3</Label>
      </StackPanel>`
    );

    sectionTitle(p2, ROW2_Y, 'StackPanel (Horizontal)');
    drawUi(
      p2,
      MARGIN,
      ROW2_Y + MM(8),
      MM(150),
      MM(12),
      `<StackPanel Orientation="Horizontal">
        <Label>Left</Label>
        <Label>Middle</Label>
        <Label>Right</Label>
      </StackPanel>`
    );

    sectionTitle(p2, ROW3_Y, 'DockPanel');
    drawUi(
      p2,
      MARGIN,
      ROW3_Y + MM(8),
      MM(120),
      MM(50),
      `<DockPanel>
        <Label Dock="Top" Height="10">Top</Label>
        <Label Dock="Bottom" Height="10">Bottom</Label>
        <Label Dock="Left" Width="30">Left</Label>
        <Label Dock="Right" Width="30">Right</Label>
        <Label>Center</Label>
      </DockPanel>`
    );

    sectionTitle(p2, ROW4_Y, 'Border');
    drawUi(
      p2,
      MARGIN,
      ROW4_Y + MM(8),
      MM(80),
      MM(40),
      `<Border BorderThickness="1" Padding="5">
        <Label>Bordered Content</Label>
      </Border>`
    );

    const p3 = pdf.addPage();
    title(p3, 'UI Module — Grid Layout');

    sectionTitle(p3, ROW1_Y, 'Grid with Rows/Columns');
    drawUi(
      p3,
      MARGIN,
      ROW1_Y + MM(8),
      MM(150),
      MM(50),
      `<Grid>
        <Grid.RowDefinitions>
          <RowDefinition Height="12"/>
          <RowDefinition Height="12"/>
        </Grid.RowDefinitions>
        <Grid.ColumnDefinitions>
          <ColumnDefinition Width="50"/>
          <ColumnDefinition Width="*"/>
        </Grid.ColumnDefinitions>
        <Label Grid.Row="0" Grid.Column="0">Name:</Label>
        <TextBox Grid.Row="0" Grid.Column="1"/>
        <Label Grid.Row="1" Grid.Column="0">Email:</Label>
        <TextBox Grid.Row="1" Grid.Column="1"/>
      </Grid>`
    );

    sectionTitle(p3, ROW2_Y, 'Grid with ColumnSpan');
    drawUi(
      p3,
      MARGIN,
      ROW2_Y + MM(8),
      MM(150),
      MM(40),
      `<Grid>
        <Grid.RowDefinitions>
          <RowDefinition Height="10"/>
          <RowDefinition Height="10"/>
        </Grid.RowDefinitions>
        <Grid.ColumnDefinitions>
          <ColumnDefinition Width="40"/>
          <ColumnDefinition Width="*"/>
        </Grid.ColumnDefinitions>
        <Label Grid.Row="0" Grid.Column="0">Title:</Label>
        <TextBox Grid.Row="0" Grid.Column="1" Grid.ColumnSpan="2"/>
        <Label Grid.Row="1" Grid.Column="0">Desc:</Label>
        <TextBox Grid.Row="1" Grid.Column="1" Grid.ColumnSpan="2"/>
      </Grid>`
    );

    sectionTitle(p3, ROW3_Y, 'WrapPanel');
    drawUi(
      p3,
      MARGIN,
      ROW3_Y + MM(8),
      MM(160),
      MM(40),
      `<WrapPanel>
        <Button Width="30">A</Button>
        <Button Width="30">B</Button>
        <Button Width="30">C</Button>
        <Button Width="30">D</Button>
        <Button Width="30">E</Button>
        <Button Width="30">F</Button>
      </WrapPanel>`
    );

    const p4 = pdf.addPage();
    title(p4, 'UI Module — Complex Controls');

    sectionTitle(p4, ROW1_Y, 'Menu');
    drawUi(
      p4,
      MARGIN,
      ROW1_Y + MM(8),
      MM(180),
      MM(10),
      `<Menu>
        <MenuItem>File</MenuItem>
        <MenuItem>Edit</MenuItem>
        <MenuItem>View</MenuItem>
        <MenuItem>Help</MenuItem>
      </Menu>`
    );

    sectionTitle(p4, ROW2_Y, 'Menu with Items');
    drawUi(
      p4,
      MARGIN,
      ROW2_Y + MM(8),
      MM(100),
      MM(35),
      `<Menu>
        <MenuItem>File
          <MenuItem>New</MenuItem>
          <MenuItem>Open</MenuItem>
          <MenuItem>Save</MenuItem>
        </MenuItem>
      </Menu>`
    );

    sectionTitle(p4, ROW3_Y, 'GroupBox');
    drawUi(
      p4,
      MARGIN,
      ROW3_Y + MM(8),
      MM(120),
      MM(50),
      `<GroupBox Header="User Info">
        <StackPanel Orientation="Vertical">
          <Label>Name:</Label>
          <TextBox Width="100"/>
        </StackPanel>
      </GroupBox>`
    );

    sectionTitle(p4, ROW4_Y, 'TabControl');
    drawUi(
      p4,
      MARGIN,
      ROW4_Y + MM(8),
      MM(130),
      MM(50),
      `<TabControl>
        <TabItem Header="Tab 1">
          <Label>Content 1</Label>
        </TabItem>
        <TabItem Header="Tab 2">
          <Label>Content 2</Label>
        </TabItem>
      </TabControl>`
    );

    const p5 = pdf.addPage();
    title(p5, 'UI Module — Annotations');

    sectionTitle(p5, ROW1_Y, 'Single Annotations');
    drawUi(
      p5,
      MARGIN,
      ROW1_Y + MM(8),
      MM(80),
      MM(12),
      '<Button Note="Submits form to server">Submit</Button>'
    );
    drawUi(
      p5,
      MARGIN + MM(85),
      ROW1_Y + MM(8),
      MM(80),
      MM(12),
      '<Button Warning="Data will be lost">Clear</Button>'
    );
    drawUi(p5, MARGIN, ROW1_Y + MM(25), MM(80), MM(12), '<TextBox Error="Email is required"/>');
    drawUi(
      p5,
      MARGIN + MM(85),
      ROW1_Y + MM(25),
      MM(80),
      MM(12),
      '<Button Good="All validations passed">Validate</Button>'
    );

    sectionTitle(p5, ROW2_Y, 'Multiple Annotations');
    drawUi(
      p5,
      MARGIN,
      ROW2_Y + MM(8),
      MM(90),
      MM(12),
      '<Button Note="Saves data" Warning="Confirm before save">Save</Button>'
    );
    drawUi(
      p5,
      MARGIN + MM(95),
      ROW2_Y + MM(8),
      MM(90),
      MM(12),
      '<TextBox Note="Username" Warning="Min 3 chars" Good="Available">John</TextBox>'
    );

    sectionTitle(p5, ROW3_Y, 'Annotations on Various Controls');
    drawUi(
      p5,
      MARGIN,
      ROW3_Y + MM(8),
      MM(70),
      MM(10),
      '<Label Note="User display name">User Name</Label>'
    );
    drawUi(
      p5,
      MARGIN + MM(75),
      ROW3_Y + MM(8),
      MM(80),
      MM(10),
      '<TextBox Error="Invalid format">invalid@</TextBox>'
    );
    drawUi(
      p5,
      MARGIN,
      ROW3_Y + MM(22),
      MM(60),
      MM(8),
      '<CheckBox Warning="Required field">Accept</CheckBox>'
    );
    drawUi(
      p5,
      MARGIN + MM(65),
      ROW3_Y + MM(22),
      MM(60),
      MM(8),
      '<RadioButton Good="Recommended option">Option A</RadioButton>'
    );

    const p6 = pdf.addPage();
    title(p6, 'UI Module — Full Form Example');

    sectionTitle(p6, ROW1_Y, 'Registration Form with Annotations');
    drawUi(
      p6,
      MARGIN,
      ROW1_Y + MM(8),
      MM(180),
      MM(100),
      `<StackPanel Orientation="Vertical">
        <Label>User Registration</Label>
        <Grid>
          <Grid.RowDefinitions>
            <RowDefinition Height="10"/>
            <RowDefinition Height="10"/>
            <RowDefinition Height="10"/>
            <RowDefinition Height="15"/>
          </Grid.RowDefinitions>
          <Grid.ColumnDefinitions>
            <ColumnDefinition Width="50"/>
            <ColumnDefinition Width="*"/>
          </Grid.ColumnDefinitions>
          <Label Grid.Row="0" Grid.Column="0">Name:</Label>
          <TextBox Grid.Row="0" Grid.Column="1" Note="Full name"/>
          <Label Grid.Row="1" Grid.Column="0">Email:</Label>
          <TextBox Grid.Row="1" Grid.Column="1" Error="Required field"/>
          <Label Grid.Row="2" Grid.Column="0">Phone:</Label>
          <TextBox Grid.Row="2" Grid.Column="1" Warning="Format: +1-234-567"/>
        </Grid>
        <CheckBox Note="Agree to terms">I agree</CheckBox>
        <StackPanel Orientation="Horizontal">
          <Button Note="Creates account">Register</Button>
          <Button Warning="Clears form">Clear</Button>
        </StackPanel>
      </StackPanel>`
    );

    sectionTitle(p6, ROW3_Y, 'Login Dialog');
    drawUi(
      p6,
      MARGIN,
      ROW3_Y + MM(8),
      MM(150),
      MM(60),
      `<Border BorderThickness="1" Padding="10">
        <StackPanel Orientation="Vertical">
          <Label>Login</Label>
          <Grid>
            <Grid.RowDefinitions>
              <RowDefinition Height="10"/>
              <RowDefinition Height="10"/>
            </Grid.RowDefinitions>
            <Grid.ColumnDefinitions>
              <ColumnDefinition Width="40"/>
              <ColumnDefinition Width="*"/>
            </Grid.ColumnDefinitions>
            <Label Grid.Row="0" Grid.Column="0">User:</Label>
            <TextBox Grid.Row="0" Grid.Column="1"/>
            <Label Grid.Row="1" Grid.Column="0">Pass:</Label>
            <TextBox Grid.Row="1" Grid.Column="1"/>
          </Grid>
          <StackPanel Orientation="Horizontal">
            <Button Note="Authenticates user">Login</Button>
            <Button>Cancel</Button>
          </StackPanel>
        </StackPanel>
      </Border>`
    );

    // Page 7: Nested Containers
    const p7 = pdf.addPage();
    title(p7, 'UI Module — Nested Containers');

    sectionTitle(p7, ROW1_Y, 'StackPanel > Grid > Border > StackPanel');
    drawUi(
      p7,
      MARGIN,
      ROW1_Y + MM(8),
      MM(180),
      MM(65),
      `<StackPanel Orientation="Vertical">
        <Label>Outer StackPanel</Label>
        <Grid>
          <Grid.RowDefinitions>
            <RowDefinition Height="Auto"/>
          </Grid.RowDefinitions>
          <Grid.ColumnDefinitions>
            <ColumnDefinition Width="80"/>
            <ColumnDefinition Width="*"/>
          </Grid.ColumnDefinitions>
          <Label Grid.Row="0" Grid.Column="0">Left Column</Label>
          <Border Grid.Row="0" Grid.Column="1" BorderThickness="1" Padding="3">
            <StackPanel Orientation="Vertical">
              <Label>Nested Label A</Label>
              <Label>Nested Label B</Label>
            </StackPanel>
          </Border>
        </Grid>
        <StackPanel Orientation="Horizontal">
          <Button>Action 1</Button>
          <Button>Action 2</Button>
          <Button>Action 3</Button>
        </StackPanel>
      </StackPanel>`
    );

    sectionTitle(p7, ROW2_Y, 'Grid: Auto + Star + Fixed columns with ColumnSpan');
    drawUi(
      p7,
      MARGIN,
      ROW2_Y + MM(8),
      MM(180),
      MM(55),
      `<Grid>
        <Grid.RowDefinitions>
          <RowDefinition Height="10"/>
          <RowDefinition Height="10"/>
          <RowDefinition Height="10"/>
        </Grid.RowDefinitions>
        <Grid.ColumnDefinitions>
          <ColumnDefinition Width="40"/>
          <ColumnDefinition Width="*"/>
          <ColumnDefinition Width="30"/>
        </Grid.ColumnDefinitions>
        <Label Grid.Row="0" Grid.Column="0">Name:</Label>
        <TextBox Grid.Row="0" Grid.Column="1"/>
        <Button Grid.Row="0" Grid.Column="2">...</Button>
        <Label Grid.Row="1" Grid.Column="0">Email:</Label>
        <TextBox Grid.Row="1" Grid.Column="1" Grid.ColumnSpan="2"/>
        <Button Grid.Row="2" Grid.Column="0" Grid.ColumnSpan="3">Submit</Button>
      </Grid>`
    );

    sectionTitle(p7, ROW3_Y, 'DockPanel with nested StackPanel in center');
    drawUi(
      p7,
      MARGIN,
      ROW3_Y + MM(8),
      MM(180),
      MM(55),
      `<DockPanel>
        <Label Dock="Top">Toolbar</Label>
        <Label Dock="Bottom">Status Bar</Label>
        <StackPanel Dock="Left" Orientation="Vertical">
          <Button>File</Button>
          <Button>Edit</Button>
          <Button>View</Button>
        </StackPanel>
        <Border BorderThickness="1">
          <StackPanel Orientation="Vertical">
            <Label>Main Content Area</Label>
            <TextBox>Content here...</TextBox>
          </StackPanel>
        </Border>
      </DockPanel>`
    );

    sectionTitle(p7, ROW4_Y, 'GroupBox wrapping a Grid');
    drawUi(
      p7,
      MARGIN,
      ROW4_Y + MM(8),
      MM(140),
      MM(50),
      `<GroupBox Header="Address">
        <Grid>
          <Grid.RowDefinitions>
            <RowDefinition Height="10"/>
            <RowDefinition Height="10"/>
            <RowDefinition Height="10"/>
          </Grid.RowDefinitions>
          <Grid.ColumnDefinitions>
            <ColumnDefinition Width="35"/>
            <ColumnDefinition Width="*"/>
          </Grid.ColumnDefinitions>
          <Label Grid.Row="0" Grid.Column="0">Street:</Label>
          <TextBox Grid.Row="0" Grid.Column="1"/>
          <Label Grid.Row="1" Grid.Column="0">City:</Label>
          <TextBox Grid.Row="1" Grid.Column="1"/>
          <Label Grid.Row="2" Grid.Column="0">ZIP:</Label>
          <TextBox Grid.Row="2" Grid.Column="1"/>
        </Grid>
      </GroupBox>`
    );

    // Page 8: Multiple Annotations
    const p8 = pdf.addPage();
    title(p8, 'UI Module — Multiple Annotations');

    sectionTitle(p8, ROW1_Y, 'Two annotations on same control');
    drawUi(
      p8,
      MARGIN,
      ROW1_Y + MM(8),
      MM(100),
      MM(14),
      '<Button Note="Saves all data" Warning="Confirm before saving">Save</Button>'
    );
    drawUi(
      p8,
      MARGIN,
      ROW1_Y + MM(26),
      MM(100),
      MM(14),
      '<TextBox Note="Enter username" Warning="Min 3 chars" Good="Available">john</TextBox>'
    );
    drawUi(
      p8,
      MARGIN,
      ROW1_Y + MM(44),
      MM(100),
      MM(14),
      '<Button Note="Submits form" Warning="Check required fields" Error="Server offline">Submit</Button>'
    );

    sectionTitle(p8, ROW2_Y, 'Annotations on nested controls in a form (notesWidth=60)');
    drawUi(
      p8,
      MARGIN,
      ROW2_Y + MM(8),
      MM(185),
      MM(75),
      `<StackPanel Orientation="Vertical">
        <Label>Account Settings</Label>
        <Grid>
          <Grid.RowDefinitions>
            <RowDefinition Height="10"/>
            <RowDefinition Height="10"/>
            <RowDefinition Height="10"/>
            <RowDefinition Height="10"/>
          </Grid.RowDefinitions>
          <Grid.ColumnDefinitions>
            <ColumnDefinition Width="45"/>
            <ColumnDefinition Width="*"/>
          </Grid.ColumnDefinitions>
          <Label Grid.Row="0" Grid.Column="0">Username:</Label>
          <TextBox Grid.Row="0" Grid.Column="1" Note="Used for login" Good="Unique"/>
          <Label Grid.Row="1" Grid.Column="0">Password:</Label>
          <PasswordBox Grid.Row="1" Grid.Column="1" Warning="Min 8 characters"/>
          <Label Grid.Row="2" Grid.Column="0">Email:</Label>
          <TextBox Grid.Row="2" Grid.Column="1" Error="Required field"/>
          <Label Grid.Row="3" Grid.Column="0">Phone:</Label>
          <TextBox Grid.Row="3" Grid.Column="1" Note="Optional" Warning="Format: +1-234-567"/>
        </Grid>
        <StackPanel Orientation="Horizontal">
          <Button Note="Creates account">Register</Button>
          <Button Warning="Clears all fields">Clear</Button>
        </StackPanel>
      </StackPanel>`,
      { notesWidth: '60' }
    );

    sectionTitle(p8, ROW4_Y, 'All annotation types on individual controls');
    drawUi(
      p8,
      MARGIN,
      ROW4_Y + MM(8),
      MM(100),
      MM(14),
      '<Label Note="Informational note">Info Label</Label>'
    );
    drawUi(
      p8,
      MARGIN + MM(105),
      ROW4_Y + MM(8),
      MM(90),
      MM(14),
      '<Label Warning="Caution required">Warning Label</Label>'
    );
    drawUi(
      p8,
      MARGIN,
      ROW4_Y + MM(26),
      MM(100),
      MM(14),
      '<Label Error="Validation failed">Error Label</Label>'
    );
    drawUi(
      p8,
      MARGIN + MM(105),
      ROW4_Y + MM(26),
      MM(90),
      MM(14),
      '<Label Good="Validated OK">Good Label</Label>'
    );
  }
}
